import { memo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import * as R from 'Router/routes';
import { Link, Placeholder, Timer } from 'components';
import H1 from 'components/H/H1';
import H2 from 'components/H/H2';
import { TwSubmitButton } from 'components/TwButton/TwButton';
import OTP from 'components/OTP/OTP';
import { NEW_CODE_TIMER, PHONE_CALL_TIMER } from '../../../constants';
import useVerification from './useVerification';

const PhoneVerification = memo(function PhoneVerificationMemo() {
  const { t } = useTranslation(['phone_verification', 'common', 'error']);
  const {
    clientPhoneIsLoading,
    formik,
    handleChange,
    getNewCode,
    getPhoneCall,
    handleOnNewCodeTimeout,
    handleOnPhoneCallTimeout,
    newCodeTimerVisible,
    phoneCallTimerVisible,
    clientPhone,
    pending,
    serverError,
    isDisabled,
  } = useVerification();

  return (
    <>
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>

      <H1>{t('verification')}</H1>
      <p
        className="max-w-[400px] text-center lg:text-left mx-auto lg:mx-0"
        data-test="subtitle"
      >
        <span className="font-light">{t('code_sent')}</span>{' '}
        <span className="text-lg">
          <Placeholder
            dataTest="phone-number"
            loading={clientPhoneIsLoading}
            placeholder="XXX XXX XXX"
          >
            {clientPhone?.mobilePhone}
          </Placeholder>
        </span>{' '}
        <Link className="text-primary" to={R.PHONE_CHANGE}>
          {t('wrong_number')}
        </Link>
      </p>

      <form
        className="flex flex-col items-center lg:items-start w-full px-5 lg:px-0 mt-6 mb-10"
        onSubmit={formik.handleSubmit}
      >
        <H2 dataTest="subheading">{t('enter_code')}</H2>
        <div className="flex flex-col items-start mb-4 justify-start w-full max-w-[400px]">
          <OTP
            dataTest="phone-verification"
            name="verificationCode"
            value={formik.values.verificationCode}
            onChange={handleChange}
            isError={
              Boolean(formik.errors.verificationCode) &&
              formik.touched.verificationCode
            }
          />
          {serverError && (
            <div
              className="text-destructive mt-4 text-center"
              data-test="phone-verification-error-server"
            >
              {serverError}
            </div>
          )}
        </div>
        <div className="max-w-[400px] w-full">
          <Timer
            timerVisible={newCodeTimerVisible}
            pending={pending}
            onAction={getNewCode}
            onTimeout={handleOnNewCodeTimeout}
            buttonText={t('phone_verification:get_new_code')}
            dataTest="phone-verification-timer-new-code"
            timerName={NEW_CODE_TIMER}
          />

          <Timer
            timerVisible={phoneCallTimerVisible}
            pending={pending}
            onAction={getPhoneCall}
            onTimeout={handleOnPhoneCallTimeout}
            buttonText={t('phone_verification:get_phone_call')}
            dataTest="phone-verification-timer-phone-call"
            timerName={PHONE_CALL_TIMER}
          />
        </div>
        <p
          className="text-muted font-light text-[12px] mt-5 mb-6 max-w-[400px]"
          dangerouslySetInnerHTML={{
            __html: t('contact_us'),
          }}
          data-test="phone-verification-message-contactus"
        />

        <TwSubmitButton
          disabled={isDisabled || pending}
          pending={pending}
          data-test="phone-verification-btn-continue"
        >
          {t('common:continue')}
        </TwSubmitButton>
      </form>
    </>
  );
});

export default PhoneVerification;
